import "@shoelace-style/shoelace/dist/components/rating/rating.js";

import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { BaseElement } from "../BaseElement";
import {
    IMedia,
    IPlace,
    IPlaceMediaItem,
    IPlaceReport,
    makeEmptyPlaceReport,
} from "../../models/Models";
import { litIf } from "../LitUtils";

import "../map/PlacesMap";
import "../CloseButton";
import "../Warning/Warning";
import "../Markdown";
import "../MediaList";
import "../Media/MediaImportDialog";
import "../PlaceDetails";
import "../PlaceId";
import "../InfoBadges";
import "../PlaceReport/PlaceReportEditor";
import "../PlaceReport/PlaceReport";
import "../Weather/Weather";
import "../Media/MediaEditor";

import { watch } from "../internal/watch";
import {
    MediaType,
    getMediaTypeFromMimeType,
    numMediaOfType,
    MEDIA_GALLERY_VIEW_TYPES,
    MEDIA_GALLERY_SURVEY_TYPES,
    PHOTO_MEDIA_TYPES,
    GEO_MEDIA_TYPES,
} from "../../Media";
import { capitalizeFirstLetter, downloadBlobToFile, getRandomInt } from "../../Utils";
import { InfoBadgeData as InfoBadgeData } from "../InfoBadges";
import { ILocation } from "../../models/ILocation";
import { styles } from "./PlaceView.style";
import { MarkdownLightStyle } from "../Markdown";
import { makePlaceInfoBadges } from "../PlaceInfoBadges";
import { LightTheme } from "../ThemeLight";
import { MediaContentType, Rating, TriState } from "../../../models";
import { distanceInBetweenLocationsMeters } from "../../models/ILocation";
import { DatabaseBackend, Kernel } from "../../Kernel";

import Fontawesome from "lit-fontawesome";
import { getPlaceWarnings, placeHasWreckData } from "../../PlaceUtils";
import { formatDate, formatDistance } from "../../Format";
import { MapType } from "../../TileLayers";
import { DataClient } from "../../DataStore/LocalDataStore";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { DataProvider } from "../../DataStore/DataProvider";
import { EditorPlaceMediaInfo, EditorPlaceMediaInfoOp } from "../PlaceEditor/PlaceEditor";
import { IPlaceTable } from "../../backend/IPlaceTable";
import { showConfirmDialog } from "../ConfirmDialog";
import { getIndexOfMediaInMediaArray, userCanEditPlaceMedia } from "../../backend/IPlaceMediaTable";
import { openAddPlaceToUserListDialog } from "../AddPlaceToUserListDialog/AddPlaceToUserListDialog";

const DEFAULT_MAP_ZOOM = 18;

export type PlaceViewMode = "normal" | "editor" | "place-report" | "media-editor";

export function showDeleteMediaDialog(
    id: string,
    preDelete: () => void = () => {},
    postDelete: () => void = () => {},
    onCancel: () => void = () => {}
) {
    showConfirmDialog(
        {
            open: true,
            prompt: "Delete media?",
            message:
                "This will irretrievably delete the media. Press 'Yes' to delete or 'Cancel' to abort.",
        },
        async () => {
            preDelete();
            const table = Kernel.instance().dataStore.getSource<IPlaceTable>("places");
            await table.deleteMediaItem(id);
            postDelete();
        },
        () => {
            onCancel();
        }
    );
}

export function showDeletePlaceReportDialog(
    id: string,
    preDelete: () => void = () => {},
    postDelete: () => void = () => {},
    onCancel: () => void = () => {}
) {
    showConfirmDialog(
        {
            open: true,
            prompt: "Delete report?",
            message:
                "This will irretrievably delete the report. Press 'Yes' to delete or 'Cancel' to abort.",
        },
        async () => {
            preDelete();
            const table = Kernel.instance().dataStore.getSource<IPlaceTable>("places");
            await table.deleteReport(id);
            postDelete();
        },
        () => {
            onCancel();
        }
    );
}

@customElement("ds-place-view")
export class PlaceView extends BaseElement implements ChangeSetListener {
    static override styles = [Fontawesome, MarkdownLightStyle, LightTheme, styles];

    @property({ type: Object }) place?: IPlace;
    @property({ type: String }) viewMode: PlaceViewMode = "normal";
    @property({ type: Boolean }) noCloseButton = false;
    @property({ type: Boolean }) noAddTripReportButton = false;
    @property({ type: Boolean }) noAddMediaButton = false;
    @property({ type: Boolean }) noShowOnMapButton = false;
    @property({ type: Boolean }) noOpenInAppButton = false;
    @property({ type: Boolean }) noAddToListButton = false;
    @property({ type: Boolean }) showEmpty = false;
    @property({ type: Array }) closestPlaces?: IPlace[];
    @property({ type: Array }) media?: IPlaceMediaItem[];
    @property({ type: Array }) reports?: IPlaceReport[];

    @state() protected _selectedPlaceDistFromGroundWater: number | undefined;
    @state() protected _bannerImage: IMedia | undefined;
    @state() protected _numImagesAndVideos = 0;
    @state() protected _numGeoFiles = 0;
    @state() protected _placeName: string | undefined;
    @state() protected _infoBadges: InfoBadgeData[] = [];
    @state() protected _currentEditModified = false;
    @state() protected _saving = false;
    @state() protected _reports: IPlaceReport[] = [];
    @state() protected _media: IPlaceMediaItem[] = [];
    @state() protected _editTripReport?: IPlaceReport;
    @state() protected _editMedia?: IPlaceMediaItem;

    private _placeTable = this._kernel.dataStore.getSource<IPlaceTable>("places");

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._kernel.dataStore.connectClient(
            new DataClient(this, [
                { source: "places", paths: [] },
                { source: "place-report", paths: [] },
                { source: "place-media", paths: [] },
            ])
        );
        if (this.viewMode === "editor") {
            this._openEditor();
        }
    }

    public override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._kernel.dataStore.disconnectClient(this);
    }

    onChangeSet(provider: DataProvider, changeSet: ChangeSet<any>): void {
        if (provider.name === "places") {
            for (const p of changeSet.updated) {
                if (p.oldValue.id === this.place?.id) {
                    this.place = p.newValue;
                }
            }
        } else {
            this._onPlaceChanged();
        }
    }

    public setPlaceLocation(location: ILocation) {
        const editor = this.shadowRoot?.getElementById("editor") as any;
        if (editor) {
            editor?.setPlaceLocation(location);
            this._currentEditModified = true;
        }
    }

    @watch("media")
    async _onMediaChanged() {
        this._media = this.media ?? [];
    }

    @watch("reports")
    async _onReportsChanged() {
        this._reports = this.reports ?? [];
    }

    @watch("place")
    async _onPlaceChanged() {
        this._bannerImage = undefined;
        this._selectedPlaceDistFromGroundWater = undefined;
        this._numImagesAndVideos = 0;
        this._numGeoFiles = 0;
        this._placeName = undefined;
        this._infoBadges = [];
        this._reports = [];
        this._media = [];

        if (this.place) {
            const place = this.place as IPlace;
            if (this.place.id.length === 0) {
                this._currentEditModified = true;
            }

            this._placeName = place.data.name;
            if (!this._placeName || this._placeName.length === 0) {
                this._placeName = place.data.spelioData.reference;
                if (!this._placeName || this._placeName.length === 0) {
                    this._placeName = "[Unnamed]";
                }
            }

            this._infoBadges = await makePlaceInfoBadges(
                this.place,
                this._media.map((pm) => pm.media)
            );
            if (this.media) {
                this._media = this.media;
            } else {
                this._media = await this._placeTable.getMediaItems(this.place.id);
            }

            if (this._media.length > 0) {
                this._bannerImage = this._chooseBannerImage(this._media);
                const a = this._media.map((m) => m.media);
                this._numImagesAndVideos = numMediaOfType(a, PHOTO_MEDIA_TYPES);
                this._numGeoFiles = numMediaOfType(a, GEO_MEDIA_TYPES);
            }

            if (this.reports) {
                this._reports = this.reports;
            } else {
                this._reports = await this._placeTable.getReports(this.place.id);
            }
        }
    }

    private _chooseBannerImage(media: IPlaceMediaItem[]): IMedia | undefined {
        if (media) {
            let photos = media.filter(
                (x) => getMediaTypeFromMimeType(x.media.mimeType) === MediaType.Image
            );

            // prefer cover images if the place has them
            const covers = photos.filter(
                (x) => x.media.contentType === MediaContentType.ARTICLE_COVER
            );
            if (covers.length > 0) {
                photos = covers;
            }

            if (photos?.length > 0) {
                return photos[getRandomInt(photos.length)].media;
            }
        }

        return undefined;
    }

    private _share() {
        this._raiseEvent("ds-share-place", { place: this.place });
    }

    private _navigateHere() {
        this._kernel.events.raiseEvent("ds-navigate", {
            location: this.place?.data.location,
        });
    }

    private _openInGallery(types: string[]) {
        this._raiseEvent("ds-open-gallery", {
            place: this.place,
            filter: types,
            media: this._media,
        });
    }

    private _openMediaInGallery(media: IMedia) {
        const mediaList = this._media.map((m) => m.media);
        const index = getIndexOfMediaInMediaArray(mediaList, media.id);

        this._raiseEvent("ds-open-gallery", {
            placeId: this.place?.id,
            filter: GEO_MEDIA_TYPES.includes(getMediaTypeFromMimeType(media.mimeType))
                ? MEDIA_GALLERY_SURVEY_TYPES
                : MEDIA_GALLERY_VIEW_TYPES,
            index: index,
            media: this._media,
        });
    }

    private _showOnMap() {
        this._raiseEvent("ds-view-on-map", {
            location: this.place?.data.location,
        });
        this._close();
    }

    private _openInApp() {
        if (this.place) {
            this._raiseEvent("ds-open-in-app", {
                placeId: this.place.id,
            });
        }
    }

    private _openInMapApp() {
        this._raiseEvent("ds-open-in-map-app", { place: this.place });
    }
    private _openEditor() {
        import("../PlaceEditor/PlaceEditor");
        this.viewMode = "editor";
    }

    private _close() {
        this._raiseEvent("ds-close");
    }

    private async _downloadMedia(media: IMedia) {
        const blob = await this._kernel.mediaLibrary.get(media.file.key);
        if (blob) {
            downloadBlobToFile(blob, media.name ?? undefined);
        }
    }

    private async _updatePlace(place: IPlace, media: EditorPlaceMediaInfo[]) {
        const updated = await this._placeTable.upsert(place);
        if (updated) {
            this.place = updated;

            // handle media changes
            for (const m of media) {
                switch (m.op) {
                    case EditorPlaceMediaInfoOp.Added:
                        {
                            await this._placeTable.createMediaItem(
                                this.place.id,
                                m.info.media,
                                m.info.groupAccess as string[]
                            );
                        }
                        break;

                    case EditorPlaceMediaInfoOp.Deleted:
                        {
                            await this._placeTable.deleteMediaItem(m.info.id);
                        }
                        break;

                    case EditorPlaceMediaInfoOp.Modified:
                        {
                            await this._placeTable.updateMediaItem(m.info);
                        }
                        break;
                }
            }
        }
    }

    private _deleteReportWithConfirmation(id: string) {
        showConfirmDialog(
            {
                open: true,
                prompt: "Delete report?",
                message:
                    "This will irretrievably delete the report. Press 'Yes' to delete or 'Cancel' to abort.",
            },
            async () => {
                this._saving = true;
                await this._placeTable.deleteReport(id);
                this._reports = this._reports.filter((r) => r.id !== id);
                this._saving = false;
            }
        );
    }
    private _deleteMediaWithConfirmation(id: string) {
        showDeleteMediaDialog(id, () => {
            this._media = this._media.filter((x) => x.id !== id);
        });
    }

    private _renderHeaderButton(icon: string, text: string, onclick: any, show = true) {
        return html`${litIf(
            show,
            () =>
                html`<div class="header-button flex-column" @click=${() => onclick()}>
                    <sl-icon-button name="${icon}" label=${text}></sl-icon-button
                    ><span>${text}</span>
                </div>`,
            () => html``
        )}`;
    }

    private _renderWeather() {
        return html` <ds-weather
            .lat=${this.place?.data.location.lat ?? 0}
            .lon=${this.place?.data.location.lon ?? 0}
        ></ds-weather>`;
    }

    private _renderSectionHeader(title: string) {
        return html`<h3 class="section-header">${title}</h3>`;
    }

    private _renderAmenities() {
        const items: { icon: string; text: string }[] = [];
        const amenities = this.place?.data.amenities;

        function addAmenity(text: string, name: string, icon: string) {
            const amenity = (amenities as any)[name] as TriState;
            if (amenity && amenity !== TriState.UNKNOWN) {
                items.push({
                    icon,
                    text: `${amenity === TriState.YES ? `Has` : `No`} ${text}`,
                });
            }
        }

        function addRating(text: string, name: string, icon: string) {
            const amenity = (amenities as any)[name] as Rating;
            if (amenity && ![Rating.UNKNOWN, Rating.NONE].includes(amenity)) {
                items.push({
                    icon,
                    text: `${capitalizeFirstLetter(amenity)} ${text}`,
                });
            }
        }

        if (amenities) {
            addAmenity("showers", "hasShowers", "fa-shower");
            addAmenity("toilets", "hasToilets", "fa-toilet");
            addAmenity("phone", "hasPhone", "fa-phone");

            if (amenities.parking && amenities.parking !== Rating.UNKNOWN) {
                addRating("parking", "parking", "fa-parking");
                addRating("food", "food", "fa-utensils");
                addRating("for family", "family", "fa-child");
            }
        }

        if (items.length > 0) {
            return html`${this._renderSectionHeader("Amenities")}
                <ul class="three-column-list">
                    ${items.map(
                        (item) =>
                            html`<li>
                                <div class="amenity">
                                    <i class="fa ${item.icon}" aria-hidden="true"></i>
                                    <span>${item.text}</span>
                                </div>
                            </li>`
                    )}
                </ul>`;
        }

        return html``;
    }

    private _renderWreckDetails() {
        if (placeHasWreckData(this.place)) {
            const wd = this.place?.data.wreckData;
            return html`${litIf(
                wd,
                () =>
                    html`${this._renderSectionHeader("Wreck details")}
                        <div class="flex-column">
                            ${litIf(
                                wd?.type,
                                () => html` <div><b>Type : </b><span>${wd?.type}</span></div> `
                            )}
                            ${litIf(
                                wd?.built,
                                () => html` <div><b>Built : </b><span>${wd?.built}</span></div>`
                            )}
                            ${litIf(
                                wd?.dateSunk,
                                () =>
                                    html`<div>
                                        <b>Date sunk : </b
                                        ><span>${formatDate(wd?.dateSunk as string)}</span>
                                    </div> `
                            )}
                            ${litIf(
                                wd?.length,
                                () =>
                                    html`<div>
                                        <b>Length : </b
                                        ><span>${formatDistance(wd?.length as number)}</span>
                                    </div> `
                            )}
                            ${litIf(
                                wd?.width,
                                () =>
                                    html`<div>
                                        <b>Width : </b
                                        ><span>${formatDistance(wd?.width as number)}</span>
                                    </div> `
                            )}
                            ${litIf(
                                wd?.draught,
                                () =>
                                    html`<div>
                                        <b>Draught : </b
                                        ><span>${formatDistance(wd?.draught as number)}</span>
                                    </div> `
                            )}
                        </div>`
            )}`;
        }
        return html``;
    }

    private _renderDisclaimer() {
        return html`<div>
            ${this._renderSectionHeader("Disclaimer")}
            <div class="disclaimer">
                <span>
                    No claim is made as to the accuracy of information listed here. Any use of the
                    information is at your own risk. You should attempt to verify all aspects prior
                    to visiting the site from other sources and operate within your training, skill
                    and according to prevailing conditions.
                </span>
            </div>
        </div>`;
    }
    private _renderWarnings() {
        if (this.place) {
            const warnings = getPlaceWarnings(this.place);
            if (warnings.length > 0) {
                return html`
                    ${this._renderSectionHeader("Warnings")}
                    <div class="flex-column">
                        ${warnings.map(
                            (x) => html`<ds-warning .warningId=${x} showDesc></ds-warning>`
                        )}
                    </div>
                `;
            }
        }
        return html``;
    }

    private _renderCommandButton(
        show: boolean,
        enabled: boolean,
        text: string,
        icon: string,
        handler: any
    ) {
        if (show) {
            return html`<li>
                <sl-button
                    ?disabled=${!enabled}
                    class="command-button"
                    size="small"
                    @click=${() => handler()}
                    ><sl-icon name=${icon}></sl-icon>${text} &gt;</sl-button
                >
            </li>`;
        }
        return html``;
    }

    private _getCommandButtons() {
        return [
            {
                show: !this.noShowOnMapButton,
                enabled: true,
                text: "Show on map",
                icon: "pin-map-fill",
                handler: () => this._showOnMap(),
            },
            {
                show: !this.noOpenInAppButton,
                enabled: true,
                text: "Open in app",
                icon: "menu-button-wide-fill",
                handler: () => this._openInApp(),
            },
            {
                show: true,
                enabled: true,
                text: "Get directions",
                icon: "car-front-fill",
                handler: () => this._openInMapApp(),
            },
            {
                show: !this.noAddMediaButton,
                enabled: this._kernel.hasUser(),
                text: "Upload photos and videos",
                icon: "image-fill",
                handler: () => {
                    this._raiseEvent("ds-import-media", {
                        place: this.place,
                    });
                },
            },
            {
                show: !this.noAddTripReportButton,
                enabled: this._kernel.hasUser(),
                text: "Add trip report",
                icon: "file-earmark-richtext-fill",
                handler: () => {
                    this._editTripReport = makeEmptyPlaceReport();
                    this.viewMode = "place-report";
                },
            },
            {
                show: !this.noAddToListButton,
                enabled: this._kernel.hasUser(),
                text: "Add to list",
                icon: "list-ul",
                handler: () => {
                    openAddPlaceToUserListDialog({
                        placeId: this.place?.id,
                        open: true,
                    });
                },
            },
            {
                show: (this._kernel.user && this._kernel.user.inGroup("editors")) as boolean,
                enabled: (this.place && this.place.id.length > 0) as boolean,
                text: "Update address",
                icon: "file-earmark-richtext-fill",
                handler: () => {
                    if (this.place) {
                        this.place.data.address = undefined;
                        this._updatePlace(this.place, []);
                    }
                },
            },
        ];
    }

    private _renderClosestPlaces() {
        if (this.closestPlaces && this.closestPlaces.length > 0) {
            return html`${this._renderSectionHeader("Places nearby")}
                <div class="closest-places-list">
                    <table class="full-width-table">
                        ${this.closestPlaces.map(
                            (x: IPlace) =>
                                html`<tr>
                                    <td>
                                        ${litIf(
                                            this._kernel.backend === DatabaseBackend.DataStore,
                                            () =>
                                                html`<a
                                                    @click=${() =>
                                                        this._kernel.events.raiseEvent(
                                                            "ds-place-selected",
                                                            x.id
                                                        )}
                                                    >${x.data.name}</a
                                                >`,
                                            () => html`<a href="/places/${x.id}">${x.data.name}</a>`
                                        )}
                                    </td>
                                    <td>${x.data.spelioData.type}</td>
                                    <td>
                                        <ds-distance
                                            .distance=${distanceInBetweenLocationsMeters(
                                                this.place?.data.location as ILocation,
                                                x.data.location
                                            )}
                                            autoUnits
                                        ></ds-distance>
                                    </td>
                                </tr>`
                        )}
                    </table>
                </div> `;
        }
        return html``;
    }

    private _renderPlaceMedia() {
        return litIf(
            this._media && this._media?.length > 0,
            () =>
                html`${this._renderSectionHeader("Media")}
                    <ds-media-list
                        .media=${this._media.map((m) => m.media)}
                        @ds-open-media=${(e: CustomEvent) =>
                            this._openMediaInGallery(e.detail.media)}
                        @ds-download-media=${(e: CustomEvent) =>
                            this._downloadMedia(e.detail.media)}
                    ></ds-media-list>`
        );
    }

    private _renderPlaceView() {
        return html` <article class="content">
                <div class="command-buttons">
                    <ul class="two-column-list">
                        ${this._getCommandButtons().map(
                            (button) =>
                                html`${this._renderCommandButton(
                                    button.show,
                                    button.enabled,
                                    button.text,
                                    button.icon,
                                    button.handler
                                )}`
                        )}
                    </ul>
                </div>
                <hr />
                <div class="stats">
                    <ds-info-badges .badges=${this._infoBadges}></ds-info-badges>
                </div>
                ${this._renderWarnings()} ${this._renderAmenities()}
                ${this._renderSectionHeader("Gallery")}
                <ds-media-carousel
                    id="media-carousel"
                    autoplay
                    hideCloseButton
                    .mediaList=${this._media.map((m) => {
                        return {
                            media: m.media,
                            editable: userCanEditPlaceMedia(this._kernel.user, m),
                        };
                    })}
                    size="thumbnail_1024px"
                    .filter=${MEDIA_GALLERY_VIEW_TYPES}
                    @ds-media-click=${(e: CustomEvent) => this._openMediaInGallery(e.detail.media)}
                    @ds-delete=${(e: Event) => {
                        if (e.eventPhase === e.AT_TARGET) {
                            const placeMedia = this._media.find(
                                (x) => x.media.id === (e as CustomEvent).detail.media.id
                            );
                            if (placeMedia) {
                                this._deleteMediaWithConfirmation(placeMedia.id);
                            }
                        }
                    }}
                    @ds-edit=${(e: CustomEvent) => {
                        if (e.detail.media) {
                            this._editMedia = this._media.find(
                                (x) => x.media.id === e.detail.media.id
                            );
                            if (this._editMedia) {
                                this.viewMode = "media-editor";
                            }
                        }
                    }}
                ></ds-media-carousel>

                ${litIf(
                    !!this.place?.data.spelioData.notes,
                    () => html`
                        ${this._renderSectionHeader("Description")}
                        <ds-markdown .markdown=${this.place?.data.spelioData.notes}></ds-markdown>
                    `
                )}
                ${this._renderWreckDetails()}
                ${litIf(
                    !!this.place?.data.spelioData.accessDetails,
                    () =>
                        html`${this._renderSectionHeader("Access")}
                            <p>${this.place?.data.spelioData.accessDetails}</p> `
                )}
                ${litIf(
                    !!this.place?.data.alternateNames && this.place?.data.alternateNames.length > 0,
                    () =>
                        html`${this._renderSectionHeader("Also known as")}
                            <p>${this.place?.data.alternateNames.join(", ")}</p>`
                )}
                ${litIf(
                    this._reports.length > 0,
                    () =>
                        html` ${this._renderSectionHeader("Trip reports")}
                            <div class="flex-column">
                                ${this._reports.map(
                                    (x) =>
                                        html`<ds-place-report
                                            .report=${x}
                                            @ds-edit=${() => {
                                                this._editTripReport = x;
                                                this.viewMode = "place-report";
                                            }}
                                            @ds-delete=${(e: Event) => {
                                                if (e.eventPhase === e.AT_TARGET) {
                                                    this._deleteReportWithConfirmation(x.id);
                                                }
                                            }}
                                        ></ds-place-report>`
                                )}
                            </div>`
                )}
                ${this._renderClosestPlaces()} ${this._renderPlaceMedia()}
                ${this._renderDisclaimer()}
            </article>
            <div class="sidebar flex-column">
                ${this._renderWeather()}
                <ds-places-map
                    noMyLocationButton
                    .mapCenter=${this.place?.data.location}
                    .mapZoom=${DEFAULT_MAP_ZOOM}
                    mapType="terrain"
                    noControls
                ></ds-places-map>
                <ds-place-id .placeId=${this.place?.id as string}></ds-place-id>
            </div>`;
    }

    private _renderEditorView() {
        return html`<div class="editor-container">
            <ds-place-editor
                class="sl-theme-light"
                id="editor"
                .place=${this.place as IPlace}
                showdelete
                noMediaGallery
                @on-cancel=${() => (this.viewMode = "normal")}
                @on-modified=${() => (this._currentEditModified = true)}
                @on-accept=${async (e: CustomEvent) => {
                    this._saving = true;
                    this._updatePlace(e.detail.place, e.detail.media);
                    this._saving = false;
                    this._currentEditModified = false;
                }}
            ></ds-place-editor>
        </div>`;
    }
    private _renderEditorViewFooter() {
        return html`<div class="editor-footer">
                <div class="flex-row" style="margin-top:0.5em;">
                    <sl-button
                        variant="default"
                        @click=${() => {
                            if (this.place?.id) {
                                this._raiseEvent("ds-delete-place", {
                                    id: this.place.id,
                                });
                            }
                        }}
                        >Delete</sl-button
                    >
                    <sl-button
                        style="margin-left:auto"
                        variant="default"
                        @click="${() => {
                            if (this.place?.id.length === 0) {
                                this._close();
                            } else {
                                this.viewMode = "normal";
                            }
                        }}"
                        >${this._currentEditModified ? "Discard changes" : "Close"}</sl-button
                    >
                    <sl-button
                        variant="success"
                        ?disabled=${!this._currentEditModified}
                        @click=${() => {
                            (this.shadowRoot?.getElementById("editor") as any)?.accept();
                            this._currentEditModified = false;
                        }}
                        >Save</sl-button
                    >
                </div>
            </div>
        </div>`;
    }

    private _renderPlaceReportView() {
        const userId = this._kernel.user?.id;
        if (this._editTripReport && userId) {
            return html`<div class="editor-container">
                <ds-place-report-editor
                    .report=${this._editTripReport}
                    .placeId=${this.place?.id}
                    ?tidal=${this.place?.data.diveData?.isTidal === TriState.YES}
                    @ds-accept=${async () => {
                        this._editTripReport = undefined;
                        this.viewMode = "normal";
                    }}
                    @ds-cancel=${() => {
                        this._editTripReport = undefined;
                        this.viewMode = "normal";
                    }}
                ></ds-place-report-editor>
            </div>`;
        }
        this._editTripReport = undefined;
        this.viewMode = "normal";
        return html``;
    }

    private _renderMediaEditor() {
        return litIf(
            this._editMedia,
            () =>
                html`<div class="editor-container">
                    <ds-media-editor
                        id="media-editor"
                        .placeMedia=${this._editMedia}
                        @ds-close=${(e: Event) => {
                            this._editMedia = undefined;
                            this.viewMode = "normal";
                            e.stopPropagation();
                        }}
                        @ds-saved=${(e: CustomEvent) => {
                            // update immediately
                            const i = this._media.findIndex((x) => x.id === e.detail.media.id);
                            if (i !== -1) {
                                const head = this._media?.slice(0, i);
                                const tail = this._media?.slice(i + 1);
                                this._media = [...(head ?? []), e.detail.media, ...(tail ?? [])];
                            }
                        }}
                        @ds-deleted=${(e: CustomEvent) => {
                            this._media = this._media.filter((x) => x.id !== e.detail.id);
                            this._editMedia = undefined;
                            this.viewMode = "normal";
                        }}
                    ></ds-media-editor>
                </div>`
        );
    }

    override render() {
        return html` ${litIf(
                this._saving,
                () =>
                    html`<sl-dialog open no-header label="Saving place...">
                        <ds-progress-spinner></ds-progress-spinner
                    ></sl-dialog>`
            )}
            <div class="container sl-theme-light">
                ${litIf(
                    !!this.place,
                    () => html`
                        <ds-fixed-panel>
                            ${litIf(
                                this.viewMode === "normal",
                                () => html`
                                    <div class="panel-footer" slot="footer">
                                        ${litIf(
                                            this._kernel.user?.canEdit(this.place?.groupAccess) ??
                                                false,
                                            () =>
                                                html`<sl-button
                                                    size="medium"
                                                    @click=${() => this._openEditor()}
                                                    ><sl-icon
                                                        slot="prefix"
                                                        name="pencil-fill"
                                                    ></sl-icon>
                                                    Edit</sl-button
                                                >`
                                        )}
                                        ${litIf(
                                            !this.noCloseButton,
                                            () =>
                                                html`<sl-button
                                                    size="medium"
                                                    variant="primary"
                                                    @click=${() => this._close()}
                                                    ><sl-icon slot="prefix" name="x-lg"></sl-icon
                                                    >Close</sl-button
                                                >`
                                        )}
                                    </div>
                                `
                            )}
                            <div class="scrolling-container" slot="body">
                                <div class="header">
                                    <div class="banner">
                                        ${litIf(
                                            !!this._bannerImage,
                                            () =>
                                                html`<ds-media
                                                    class="banner-image"
                                                    .media=${this._bannerImage}
                                                    noHeader
                                                ></ds-media>`,
                                            () => html` <div class="banner-no-image"></div>`
                                        )}
                                        <div class="banner-footer"></div>
                                    </div>
                                    <div class="info">
                                        <h1 class="info-place-name">${this._placeName}</h1>

                                        <ds-place-details .place=${this.place}></ds-place-details>
                                    </div>
                                    <div class="buttons">
                                        ${this._renderHeaderButton(
                                            "images",
                                            `Photos (${this._numImagesAndVideos})`,
                                            () => this._openInGallery(MEDIA_GALLERY_VIEW_TYPES),
                                            this._numImagesAndVideos > 0
                                        )}
                                        ${this._renderHeaderButton(
                                            "map",
                                            `Survey (${this._numGeoFiles})`,
                                            () => this._openInGallery(MEDIA_GALLERY_SURVEY_TYPES),
                                            this._numGeoFiles > 0
                                        )}
                                        ${this._renderHeaderButton("send-fill", `Navigate`, () =>
                                            this._navigateHere()
                                        )}
                                        ${this._renderHeaderButton("share", `Share`, () =>
                                            this._share()
                                        )}
                                    </div>
                                    <div class="bookmark-button">
                                        <sl-tooltip content="Add to list">
                                            <i
                                                class="fa fa-bookmark"
                                                aria-hidden="true"
                                                @click=${() =>
                                                    openAddPlaceToUserListDialog({
                                                        placeId: this.place?.id,
                                                        open: true,
                                                    })}
                                            ></i
                                        ></sl-tooltip>
                                    </div>
                                    ${litIf(
                                        !this.noCloseButton,
                                        () => html`<ds-close-button></ds-close-button>`
                                    )}
                                </div>
                                <div class="body flex-row view-container">
                                    ${choose(this.viewMode, [
                                        ["normal", () => this._renderPlaceView()],
                                        ["editor", () => this._renderEditorView()],
                                        ["place-report", () => this._renderPlaceReportView()],
                                        ["media-editor", () => this._renderMediaEditor()],
                                    ])}
                                </div>
                            </div>
                        </ds-fixed-panel>
                    `
                )}
                ${choose(this.viewMode, [
                    [
                        "editor",
                        () => html`<div class="footer">${this._renderEditorViewFooter()}</div>`,
                    ],
                ])}
            </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-place-view": PlaceView;
    }
}
